import React, { memo, useCallback, useEffect, useState } from 'react';
import { FlatList } from 'react-native-gesture-handler';
import { Pressable, useWindowDimensions } from 'react-native';
import { Text, Box, Button, Icon, Image } from '@bonanza-social/moonlight';
import { SelectProps, SelectableOptionItem } from './Select.types';

function Select({
  selectableOptions = [],
  defaultValue,
  centerContent = false,
  allowEmptyField = true,
  clearSelectedValue,
  maxHeight = 300,
  header,
  headerKey,
  headerNS,
  buttonKey,
  buttonNS,
  onSelect,
  onPress,
  close,
}: SelectProps) {
  const [selectedOption, setSelectedOption] =
    useState<SelectableOptionItem | null>(defaultValue ?? null);

  const { height } = useWindowDimensions();
  const dynamicHeight =
    typeof maxHeight === 'string' && maxHeight.endsWith('%')
      ? (parseFloat(maxHeight) / 100) * height
      : maxHeight;

  const handleOptionPress = useCallback(
    (selectedValue: SelectableOptionItem) => {
      if (
        selectedOption?.text === selectedValue?.text &&
        (!selectedOption?.image?.source ||
          selectedOption?.image?.source === selectedValue?.image?.source) &&
        (!selectedOption.icon || selectedOption.icon === selectedValue?.icon)
      ) {
        setSelectedOption(null);
        onPress?.(null);
      } else {
        onPress?.(selectedValue);
        setSelectedOption(selectedValue);
      }
    },
    [selectedOption, onPress],
  );

  const handleSave = useCallback(() => {
    if ((allowEmptyField && clearSelectedValue) || !!selectedOption) {
      onSelect?.(selectedOption);
    }
    close();
  }, [allowEmptyField, clearSelectedValue, selectedOption, onSelect, close]);

  const getBoxStyles = useCallback((isSelected: boolean) => {
    return isSelected
      ? {
          variant: 'gradient',
          gradient: 'borderAccent',
          borderColor: 'borderSecondary',
        }
      : {
          variant: 'default',
          backgroundColor: 'mutedBackground',
        };
  }, []);

  useEffect(() => {
    if (clearSelectedValue) {
      setSelectedOption(null);
    }
  }, [clearSelectedValue]);

  const renderItem = useCallback(
    ({ item }: { item: SelectableOptionItem }) => {
      const isSelected =
        item.text === selectedOption?.text &&
        (!item?.image?.source ||
          item?.image?.source === selectedOption?.image?.source) &&
        (!item.icon || item.icon === selectedOption?.icon);

      const boxStyles = getBoxStyles(isSelected);

      return (
        <Pressable onPress={() => handleOptionPress(item)} testID="bottom-sheet-select-option">
          <Box
            padding="space-300"
            borderBottomWidth="border-width-025"
            borderRadius="radius-200"
            centerContent={centerContent}
            marginBottom="space-200"
            {...boxStyles}
          >
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="space-100"
            >
              {item.image && (
                <Image
                  source={item.image.source}
                  width={item.image.width || 20}
                  height={item.image.height || 10}
                />
              )}
              {item.icon && <Icon name={item.icon} />}
              <Text
                color={
                  isSelected ? 'accentSurfacePrimary' : 'darkSurfacePrimary'
                }
                variant="caption-semi"
              >
                {item.text}
              </Text>
            </Box>
          </Box>
        </Pressable>
      );
    },
    [handleOptionPress, selectedOption, centerContent, getBoxStyles],
  );

  return (
    <Box marginTop="space-300">
      {header || (headerNS && headerKey) ? (
        <Text
          color="darkSurfacePrimary"
          variant="h5"
          locNS={headerNS}
          locKey={headerKey}
        >
          {header}
        </Text>
      ) : null}
      <Box maxHeight={dynamicHeight} marginTop="space-300">
        <FlatList
          data={selectableOptions}
          keyExtractor={(item, index) => `${item.text}-${index}`}
          renderItem={renderItem}
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled={true}
          keyboardShouldPersistTaps="handled"
        />
        <Box padding="space-400" marginTop="space-400">
          <Button
            onPress={handleSave}
            locNS={buttonNS}
            locKey={buttonKey}
            variant="primary"
          />
        </Box>
      </Box>
    </Box>
  );
}

export default memo(Select);
